import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'

/**
 * Envelope the `deletehuntplan` callable resolves with. A refusal or a server
 * failure arrives as a resolved `ERROR` rather than a throw, and a plan that is
 * already gone answers `SUCCESS` with `removed.plan` false.
 */
export interface DeleteHuntPlanResponse {
  status: 'SUCCESS' | 'ERROR'
  errorCode?: 'INVALID_REQUEST' | 'NOT_LEADER' | 'ALREADY_STARTED'
  message?: string
  removed?: { plan: boolean; invites: number }
}

/** Only an explicit SUCCESS is a delete; anything else throws for the mutation layer. */
export function unwrapDeleteHuntPlanResponse(data: DeleteHuntPlanResponse | null | undefined): void {
  if (data?.status === 'SUCCESS') return
  // The code rather than the function's message, which is backend wording.
  throw new Error(`deletehuntplan refused the delete: ${data?.errorCode ?? data?.status ?? 'no answer'}`)
}

/**
 * A hunt leader's delete of a plan that never started, through the
 * `deletehuntplan` callable (europe-west1) the mobile app calls. Not the .NET
 * API: its plan delete leaves every invitation to the plan behind, and a plan
 * deleted on its own stays in each invitee's lists.
 *
 * The name is ALL LOWERCASE on the backend, like `removegroup`. Mirrors
 * `useDeleteGroup`: the Functions SDK is imported lazily.
 */
export function useDeleteHuntPlan() {
  const qc = useQueryClient()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  return useMutation({
    mutationFn: async ({ huntPlanId }: { groupId: string; huntPlanId: string }): Promise<void> => {
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      const call = httpsCallable<{ huntPlanId: string }, DeleteHuntPlanResponse>(fns, 'deletehuntplan')
      const res = await call({ huntPlanId })
      unwrapDeleteHuntPlanResponse(res.data)
    },
    // The lists that show a plan before it starts. Awaited, and refetched even
    // when nothing on screen reads them, so a caller that leaves for one of them
    // lands after the plan has gone from it.
    onSuccess: async (_data, { groupId }) => {
      await Promise.all(
        [
          ['clubhouse', 'me', 'hunts', 'upcoming'],
          ['clubhouse', 'me', 'hunts', 'stats'],
          ['clubhouse', 'groups', groupId, 'hunts', uid],
        ].map((queryKey) => qc.invalidateQueries({ queryKey, refetchType: 'all' })),
      )
    },
    onError: (e) => {
      console.error('Failed to delete the hunt plan', e)
    },
  })
}
